/**
 * SurveyPreview — read-only outline of a survey for mystery shoppers.
 *
 * Loads the same public bundle as SurveyForm.js but renders only the question
 * structure (labels, sub-text, required marker, type hint). No input controls,
 * no submit, no nav buttons. Gated by the per-survey `allow_public_preview`
 * toggle — when false, a "Preview not available" screen renders so the URL
 * doesn't leak the structure of non-mystery surveys.
 *
 * URL: /survey/:branchId/:code/preview
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Eye } from 'lucide-react';
import { computeQuestionNumbers } from '@/lib/questionNumbering';
import { cleanQuestionLabel } from '@/lib/answerFormatting';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map question type → human label shown as a small badge so the shopper knows
// what kind of response is expected. Intentionally generic — no scoring info.
const TYPE_LABELS = {
  nps: 'NPS score (0–10)',
  star_rating: 'Star rating',
  rating_scale: 'Rating scale',
  yes_no: 'Yes / No',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  button_choice: 'Button choice',
  short_text: 'Short text',
  long_text: 'Long text',
  email: 'Email',
  text: 'Text',
  voice: 'Voice recording',
  number: 'Number',
  contact_info: 'Contact info',
  file_upload: 'File upload',
  matrix: 'Matrix grid',
  confirmation: 'Confirmation',
};

// Blocks that are structural, not actual questions to answer.
const STRUCTURAL_TYPES = new Set([
  'section_header', 'rich_text_block', 'image_display',
  'welcome', 'thank_you',
]);

export default function SurveyPreview() {
  const { branchId, code } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await fetch(`${API}/public/survey/${branchId}/${code}`);
        if (!res.ok) {
          if (!cancel) setError('not_found');
          return;
        }
        const data = await res.json();
        if (!cancel) setBundle(data);
      } catch (_e) {
        if (!cancel) setError('not_found');
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [branchId, code]);

  const questions = useMemo(() => bundle?.questions || [], [bundle]);
  const numberMap = useMemo(() => computeQuestionNumbers(questions), [questions]);
  const allowed = bundle?.survey?.allow_public_preview;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="w-6 h-6 animate-spin text-[#1A3E6F]" />
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
        <div className="text-center max-w-md">
          <h1 className="text-lg font-bold text-[#0F172A] mb-2">Preview not available</h1>
          <p className="text-sm text-[#64748B]">This survey preview link is invalid or expired.</p>
        </div>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4" data-testid="preview-disabled">
        <div className="text-center max-w-md">
          <h1 className="text-lg font-bold text-[#0F172A] mb-2">Preview not available</h1>
          <p className="text-sm text-[#64748B]">
            The owner of this survey has not enabled the public preview. Please use the survey link to submit a response.
          </p>
        </div>
      </div>
    );
  }

  const tenant = bundle?.tenant || {};
  const branch = bundle?.branch || {};
  const survey = bundle?.survey || {};

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="survey-preview-page">
      {/* Header — mirrors the live form's branding strip. */}
      <header className="bg-white border-b border-[#E2E8F0] px-4 py-3">
        <div className="max-w-md md:max-w-3xl mx-auto flex items-center gap-3">
          {tenant.logo_url && (
            <img src={tenant.logo_url} alt={tenant.name} className="h-8 w-auto" />
          )}
          <div className="min-w-0">
            <p className="text-[11px] text-[#64748B] truncate">{branch.name}</p>
            <p className="text-sm font-bold text-[#0F172A] truncate">{tenant.name}</p>
          </div>
        </div>
      </header>

      {/* Preview-only banner. */}
      <div className="bg-[#FEF7EC] border-b border-[#F4D484] px-4 py-2">
        <div
          className="max-w-md md:max-w-3xl mx-auto flex items-center gap-2 text-xs text-[#92400E]"
          data-testid="preview-banner"
        >
          <Eye className="w-4 h-4 shrink-0" />
          <p>
            <strong>Preview only.</strong> Use the survey QR code or live link to submit a response — this view does not record answers.
          </p>
        </div>
      </div>

      {/* Outline body */}
      <main className="px-4 py-6">
        <div className="max-w-md md:max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-5 md:p-7 space-y-5">
          {survey.name && (
            <div className="border-b border-[#F1F5F9] pb-4">
              <h1 className="text-lg md:text-xl font-bold text-[#0F172A]" data-testid="preview-survey-name">
                {survey.name}
              </h1>
              {survey.description && (
                <p className="text-xs text-[#64748B] mt-1">{survey.description}</p>
              )}
              <p className="text-[11px] text-[#94A3B8] mt-2">
                {questions.filter(q => !STRUCTURAL_TYPES.has(q.type)).length} questions ·
                {' '}{questions.filter(q => q.type === 'section_header').length} sections
              </p>
            </div>
          )}

          {questions.map((q) => {
            const num = numberMap[q.id];

            if (q.type === 'section_header') {
              return (
                <div
                  key={q.id}
                  className="border-l-4 border-[#1A3E6F] bg-[#F1F5F9] rounded-r-md px-4 py-3 mt-6"
                  data-testid={`preview-section-${q.id}`}
                >
                  <p className="text-[10px] text-[#64748B] font-semibold uppercase tracking-wider">Section{num ? ` ${num}` : ''}</p>
                  <h2 className="text-base font-bold text-[#0F172A] mt-1">{cleanQuestionLabel(q.text)}</h2>
                  {q.sub_text && <p className="text-xs text-[#64748B] mt-1">{cleanQuestionLabel(q.sub_text)}</p>}
                </div>
              );
            }

            // Structural blocks other than section_header: render their label
            // only — they're informational on the live form (welcome,
            // thank_you, rich_text_block, image_display). No type badge.
            if (STRUCTURAL_TYPES.has(q.type)) {
              return (
                <div key={q.id} className="text-xs text-[#94A3B8] italic px-2" data-testid={`preview-block-${q.id}`}>
                  [{q.type.replace('_', ' ')}] {cleanQuestionLabel(q.text) || ''}
                </div>
              );
            }

            return (
              <div
                key={q.id}
                className="py-3 border-b border-[#F1F5F9] last:border-b-0"
                data-testid={`preview-question-${q.id}`}
              >
                <div className="flex items-start gap-3">
                  {num && (
                    <span className="text-xs font-semibold text-[#1A3E6F] mt-0.5 shrink-0">{num}.</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#0F172A]">
                      {cleanQuestionLabel(q.text)}
                      {q.is_required && <span className="text-[#D62B27] ml-1">*</span>}
                    </p>
                    {q.sub_text && (
                      <p className="text-xs text-[#64748B] mt-1">{cleanQuestionLabel(q.sub_text)}</p>
                    )}
                    {q.guidance_note && (
                      <p className="text-[11px] text-[#94A3B8] mt-1 italic">{cleanQuestionLabel(q.guidance_note)}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="inline-block text-[10px] font-semibold text-[#475569] bg-[#F1F5F9] rounded-full px-2 py-0.5">
                        {TYPE_LABELS[q.type] || q.type}
                      </span>
                      {q.is_required && (
                        <span className="inline-block text-[10px] font-semibold text-[#D62B27] bg-[#FEF2F2] rounded-full px-2 py-0.5">
                          Required
                        </span>
                      )}
                      {q.commentable && (
                        <span className="inline-block text-[10px] text-[#64748B] bg-[#F1F5F9] rounded-full px-2 py-0.5">
                          Comment{q.comment_required && q.is_required ? ' (required)' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-center text-[10px] text-[#94A3B8] mt-6">
          Preview generated from the survey configuration. Score values and conditional logic are hidden.
        </p>
      </main>
    </div>
  );
}
